<template>
  <v-container fluid>
    <v-row>
      <v-col cols="11" offset-lg="1">
        <div class="headline mb-3">広告ウィジェット一覧</div>
        <permission-guard situation="widget_management">
          <v-btn
            color="primary"
            dark
            tile
            class="ma-2"
            to="/admin/posts/widgets/new"
          >
            新規作成
          </v-btn>
          <v-layout row>
            <v-text-field
              v-model="query.name_cont"
              label="ウィジェット名"
              style="width: 100px"
              class="ml-2 caption"
              clearable
            />
            <v-select
              v-model="query.device_eq"
              :items="devices"
              item-value="intValue"
              style="width: 100px"
              class="ml-2"
              label="デバイス"
              clearable
            />
            <v-select
              v-model="query.morality_eq"
              :items="moralities"
              item-value="intValue"
              style="width: 100px"
              class="ml-2"
              label="道徳性"
              clearable
            />
            <v-select
              v-model="query.page_eq"
              :items="pages"
              item-value="intValue"
              style="width: 100px"
              class="ml-2"
              label="表示ページ"
              clearable
            />
            <v-select
              v-model="query.position_eq"
              :items="positions"
              item-value="intValue"
              style="width: 150px"
              class="ml-2"
              label="表示位置"
              clearable
            />
            <v-text-field
              v-model="query.page_number_eq"
              label="ページ番号"
              class="ml-2"
              required
              style="width: 150px"
              clearable
            />
            <v-btn
              color="primary"
              dark
              tile
              outlined
              class="mx-2"
              @click="search"
            >
              絞り込む
            </v-btn>
          </v-layout>
          <v-simple-table fixed-header>
            <template>
              <thead>
                <tr>
                  <th class="text-center" style="width: 30px">ID</th>
                  <th class="text-center" style="width: 120px">
                    ウィジェット名
                  </th>
                  <th style="width: 80px">表示位置</th>
                  <th>表示デバイス</th>
                  <th>表示ページ</th>
                  <th>道徳性</th>
                  <th class="text-center" />
                  <th class="text-center" />
                </tr>
              </thead>
              <tbody>
                <tr v-for="(widget, index) in widgets" :key="widget.id">
                  <td>{{ widget.id }}</td>
                  <td>{{ widget.name }}</td>
                  <td>{{ widgetI18ns.position[replaceKey(widget.position)] }}</td>
                  <td>{{ widgetI18ns.device[widget.device] }}</td>
                  <td>{{ widgetI18ns.page[widget.page] }}</td>
                  <td>{{ widgetI18ns.morality[widget.morality] }}</td>
                  <td class="text-center">
                    <v-btn
                      tile
                      outlined
                      x-small
                      color="primary"
                      :to="widgetEditPath(widget)"
                    >
                      <v-icon left small> mdi-pencil </v-icon>編集
                    </v-btn>
                  </td>
                  <td class="text-center">
                    <v-btn
                      tile
                      outlined
                      x-small
                      color="danger"
                      @click="deleteWidget(widget, index)"
                    >
                      <v-icon left small> mdi-delete </v-icon>削除
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <infinite-loading
          spinner="spiral"
          class="mt-2"
          :identifier="infiniteId"
          @infinite="infiniteHandler"
          >
            <template v-slot:no-more>
              <v-chip label outlined> 全て表示されました。 </v-chip>
            </template>
            <template v-slot:no-results>
              <v-chip label outlined> 表示できるものがありません。 </v-chip>
            </template>
          </infinite-loading>
        </permission-guard>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios'
import InfiniteLoading from 'vue-infinite-loading'
import Qs from 'qs'
import PermissionGuard from '@/components/commons/PermissionGuard'
import headUtilsMixin from '@/mixins/head_utils'
import { mapState } from 'vuex'
import { deserialize } from 'deserialize-json-api'
import snakecaseKeys from 'snakecase-keys'

export default {
  components: {
    PermissionGuard, InfiniteLoading
  },
  filters: {
    truncate: function (value) {
      if (!value) {
        return null
      }
      const length = 20
      const ommision = '...'
      if (value.length <= length) {
        return value
      }
      return value.substring(0, length) + ommision
    }
  },
  mixins: [headUtilsMixin],
  data: () => {
    return {
      pageTitle: 'ウィジェット一覧',
      widgets: [],
      infiniteId: +new Date(),
      page: 1,
      widget: {},
      devices: [
        { text: '共通', value: 'all_device', intValue: 0 },
        { text: 'SP（モバイル）', value: 'mobile', intValue: 2 },
        { text: 'PC・その他', value: 'other_device', intValue: 1 }
      ],
      pages: [
        { text: '全てのページ', value: 'all_page', intValue: 0 },
        { text: 'トップページ', value: 'top_page', intValue: 1 },
        { text: '記事ページ', value: 'posts_show', intValue: 3 }
      ],
      moralities: [
        { text: '共通表示', value: 'either_morality', intValue: 0 },
        { text: '道徳的なページのみ表示', value: 'moral', intValue: 1 },
        { text: '非道徳的なページのみ表示', value: 'immoral', intValue: 2 }
      ],
      positions: [
        { text: 'headタグ内', value: 'in_head', intValue: 1 },
        { text: 'bodyタグ内', value: 'in_body', intValue: 2 },
        { text: '目次上', value: 'under_content_header', intValue: 3 },
        { text: '3番目h2前', value: 'before_third_h2_content', intValue: 16 },
        { text: '5番目h2前', value: 'before_fifth_h2_content', intValue: 17 },
        { text: 'コンテンツ上', value: 'on_content', intValue: 4 },
        { text: 'コンテンツ内1', value: 'in_content_1', intValue: 5 },
        { text: 'コンテンツ内2', value: 'in_content_2', intValue: 6 },
        { text: 'コンテンツ内3', value: 'in_content_3', intValue: 7 },
        { text: 'コンテンツ下', value: 'under_content', intValue: 8 },
        {
          text: 'コンテンツフッター下',
          value: 'under_content_footer',
          intValue: 9
        },
        { text: 'サイドバー上', value: 'on_sidebar', intValue: 10 },
        { text: 'サイドバー中', value: 'in_sidebar', intValue: 11 },
        { text: 'サイドバー下', value: 'under_sidebar', intValue: 12 },
        { text: 'bodyタグ内最上部', value: 'top_body', intValue: 13 },
        { text: 'コンテンツ最後部', value: 'content_bottom', intValue: 14 },
        { text: 'フッター上', value: 'footer_top', intValue: 15 }
      ],
      query: {
        name_cont: null,
        page_eq: null,
        device_eq: null,
        morality_eq: null,
        position_eq: null,
        page_number_eq: null
      }

    }
  },
  computed: {
    ...mapState({
      widgetI18ns: (state) => snakecaseKeys(state.I18n.enums.widget)
    })
  },
  methods: {
    search () {
      this.page = 1
      this.widgets = []
      this.infiniteId++
    },
    infiniteHandler ($state) {
      axios
        .get('/api/widgets', {
          params: {
            page: this.page,
            q: this.query
          },
          paramsSerializer: function (params) {
            return Qs.stringify(params, { arrayFormat: 'brackets' })
          }
        })
        .then(({ data }) => {
          const desirializedData = deserialize(data).data
          if (desirializedData.length) {
            this.page++
            this.widgets = this.widgets.concat(desirializedData)
            $state.loaded()
          } else {
            $state.complete()
          }
        })
    },
    deleteWidget (widget, index) {
      if (confirm('Are you sure?')) {
        axios.delete(`/api/widgets/${widget.id}`).then(
          () => {
            this.widgets.splice(index, 1)
            const payload = {
              status: 200,
              message: '削除しました。'
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          },
          (error) => {
            const payload = {
              status: error.response.status,
              message: error.response.data.errors.toString()
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          }
        )
      }
    },
    widgetEditPath (widget) {
      return `/admin/posts/widgets/${widget.id}/edit`
    },
    replaceKey (value) {
      return value.replace(/(.*)_(\d{1})/, '$1$2')
    }
  }
}
</script>

<style scoped>
th,
td {
  vertical-align: middle;
}
</style>
